import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import useRequest from 'hooks/useRequest';
import MeetingService from 'services/MeetingService';
import { useAuthContext } from 'AuthContext';
import moment from 'moment';
import Spinner from 'components/shared/Spinner';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import SignInLink from 'components/shared/SignInLink';
import MeetingsTable from './MeetingsTable';
import { MeetingView } from 'store/types/Meeting';

const MeetingListPage: React.FunctionComponent = () => {
  const { user, userLoading, profile, profileLoading, isStaff } = useAuthContext();
  const showNonProgramMemberPage = profile?.customer?.isProgramMember === false;
  const getMeetingsRequest = useMemo(
    () => (user && profile && !showNonProgramMemberPage ? MeetingService.getMeetings : undefined),
    [profile, showNonProgramMemberPage, user]
  );
  const { data, loading, refetch } = useRequest<MeetingView[]>(getMeetingsRequest);
  const [futureMeetings, setFutureMeetings] = useState<MeetingView[]>([]);
  const [pastMeetings, setPastMeetings] = useState<MeetingView[]>([]);
  useEffect(() => {
    if (data) {
      setFutureMeetings(
        data.filter((meeting) => moment(meeting.endDate).isSameOrAfter(moment().subtract(2, 'weeks'), 'weeks'))
      );
      setPastMeetings(
        data.filter((meeting) => moment(meeting.endDate).isBefore(moment().subtract(2, 'weeks'), 'weeks'))
      );
    }
  }, [data]);

  useEffect(() => {
    document.title = 'Meetings - SEMI Portal';
  }, []);

  return (
    <div id="meeting-list-page" className={`container ${userLoading || profileLoading ? 'emptySpacer' : ''} my-5`}>
      <Spinner loading={userLoading || profileLoading} transparent={false} className="h-100">
        {!user ? (
          <Alert severity="error" className="mb-3">
            Your session has expired or you are not logged in. Please <SignInLink>Sign In</SignInLink> and try again.
          </Alert>
        ) : showNonProgramMemberPage ? (
          <GridContainer>
            <GridItem>
              <Alert severity="info" className="mb-3">
                You are currently not a SEMI Standards Program Member.<br></br>
                Please{' '}
                <a
                  href="https://www.semi.org/en/products-services/standards/membership-application"
                  target="_blank"
                  rel="noreferrer"
                >
                  register here
                </a>{' '}
                if you wish to participate in SEMI Standards meetings.
              </Alert>
            </GridItem>
          </GridContainer>
        ) : (
          <GridContainer>
            <GridItem>
              <MeetingsTable
                label="Upcoming Meetings"
                data={futureMeetings}
                loading={loading}
                refetch={refetch}
                allowCreate
              />
            </GridItem>
            {isStaff && (
              <GridItem>
                <MeetingsTable
                  label="Past Meetings"
                  data={pastMeetings}
                  loading={loading}
                  refetch={refetch}
                  defaultSort={{ column: 'startDate', direction: 'desc' }}
                  showPagination
                />
              </GridItem>
            )}
          </GridContainer>
        )}
      </Spinner>
    </div>
  );
};

export default MeetingListPage;
